import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'foundations-theming/utils';
import EPNavigationItem from './EPNavigationItem';
import { View } from 'foundations-components/components/View';
import { List } from 'foundations-components/transitional/components/List';
import { EPNavigationDropdownBackgroundColorToken, EPNavigationDropdownBorderColorToken, EPNavigationTextColorToken, StyledEPNavigationDropdownButton, topLevelButtonLineHeightToken } from './EPNavigationStyledButtons';
import { useButtonFocusStyles } from '../utils/buttonUtils';
import { DownIcon } from 'foundations-assets/icons/components/DownIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SubNavigationWrapper = styled.div.withConfig({
  displayName: "EPNavigationDropdown__SubNavigationWrapper",
  componentId: "sc-1iuaumc-0"
})(({
  $level,
  theme
}) => {
  const wrapperBlockPadding = theme.space['200'];
  return Object.assign({
    zIndex: 9999,
    padding: `${wrapperBlockPadding} 0`,
    position: 'absolute',
    whiteSpace: 'nowrap',
    backgroundColor: theme.color[EPNavigationDropdownBackgroundColorToken],
    borderColor: theme.color[EPNavigationDropdownBorderColorToken],
    borderStyle: 'solid',
    borderWidth: theme.borderWidth['100'],
    borderRadius: theme.borderRadius['100'],
    boxShadow: theme.boxShadow['400']
  }, $level === 2 && {
    // @bend-expect trellis-tokenized
    top: `calc(${theme.space[topLevelButtonLineHeightToken]} + ${theme.space['100']})`,
    lineHeight: theme.space['600'],
    left: 0,
    minWidth: '190px'
  }, $level === 3 && {
    position: 'absolute',
    left: `calc(100% + ${theme.space['100']})`,
    // Negative top to line up the first item of the subnav with its parent item
    top: `-${wrapperBlockPadding}`,
    minWidth: '190px'
  });
});
const SubNavigation = ({
  level,
  navItems
}) => {
  const [nestedNavActiveItem, setNestedNavActiveItem] = useState('');
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx(SubNavigationWrapper, {
      $level: level,
      role: "menu",
      children: /*#__PURE__*/_jsx(List, {
        styled: false,
        role: "none",
        margin: "0",
        padding: "0",
        children: navItems.map((item, index) => {
          const {
            id,
            nestedChildren
          } = item;
          const key = `${id}-${index}`;
          if (id === 'separator') {
            return /*#__PURE__*/_jsx(View, {
              borderBlockStartColor: EPNavigationDropdownBorderColorToken,
              borderBlockStartStyle: "solid",
              borderBlockStartWidth: "100",
              marginBlock: "200",
              role: "separator"
            }, key);
          }
          function handleNavItemClick() {
            setNestedNavActiveItem(id);
          }
          const navItem = Object.assign({}, item);

          // If an item has nestedChildren, we should be expanding the nav on click
          if (nestedChildren) {
            navItem.onClick = handleNavItemClick;
          }
          return /*#__PURE__*/_jsxs(View, {
            onMouseEnter: handleNavItemClick,
            position: "relative",
            children: [/*#__PURE__*/_jsx(EPNavigationItem, {
              navItem: navItem
            }), !!nestedChildren && nestedNavActiveItem === id && /*#__PURE__*/_jsx(SubNavigation, {
              navItems: nestedChildren,
              level: level + 1
            })]
          }, key);
        })
      })
    })
  );
};
const EPNavigationDropdown = ({
  activeItem,
  clearActiveItem,
  navGroup,
  setActiveItem
}) => {
  const {
    id: navGroupId,
    text,
    children
  } = navGroup;
  const isActiveItem = activeItem === navGroupId;
  const handleSetActiveItem = useCallback(() => {
    setActiveItem(navGroupId);
  }, [navGroupId, setActiveItem]);
  const handleNavDropdownClick = useCallback(() => {
    if (!isActiveItem) {
      handleSetActiveItem();
    } else {
      clearActiveItem();
    }
  }, [clearActiveItem, handleSetActiveItem, isActiveItem]);
  const {
    focusRingStyles,
    mergedProps
  } = useButtonFocusStyles({
    disabled: false,
    onClick: handleNavDropdownClick
  });
  const handleMouseEnter = useCallback(() => {
    if (activeItem) {
      handleSetActiveItem();
    }
  }, [activeItem, handleSetActiveItem]);
  return /*#__PURE__*/_jsxs(View, {
    role: "none",
    "aria-haspopup": "true",
    "aria-expanded": isActiveItem,
    position: "relative",
    children: [/*#__PURE__*/_jsxs(StyledEPNavigationDropdownButton, Object.assign({
      $isActive: isActiveItem,
      "aria-disabled": false,
      onMouseEnter: handleMouseEnter,
      "data-nav-item": navGroupId,
      "data-test-id": navGroupId,
      $focusRingStyles: focusRingStyles
    }, mergedProps, {
      children: [/*#__PURE__*/_jsx(View, {
        paddingInline: "200",
        children: text
      }), /*#__PURE__*/_jsx(DownIcon, {
        color: EPNavigationTextColorToken,
        size: "300"
      })]
    })), isActiveItem && !!children && /*#__PURE__*/_jsx(SubNavigation, {
      navItems: children,
      level: 2
    })]
  }, navGroupId);
};
EPNavigationDropdown.propTypes = {
  activeItem: PropTypes.string,
  navGroup: PropTypes.object.isRequired,
  onActiveItemChange: PropTypes.func
};
export default EPNavigationDropdown;